import { money, signed } from "./api";

export type ChoiceEvaluation = {
  message: string;
  tone: "positive" | "negative" | "neutral";
  improvement: number;
  gap: number;
  bestActions: string[];
  assessed: boolean;
};
export type ChoiceStatistics = {
  total: number;
  choices: {
    id: string;
    label: string;
    count: number;
    percent: number;
    mostSelected: boolean;
  }[];
};
export type DailyStanding = {
  rank: number;
  participants: number;
  tied: boolean;
  final: boolean;
};

export function EvaluationDetail({
  evaluation,
}: {
  evaluation?: ChoiceEvaluation;
}) {
  if (!evaluation?.assessed) return null;
  const names: Record<string, string> = {
    sell: "전량 매도",
    half: "절반 매도",
    hold: "보유 유지",
    buy: "추가 매수",
  };
  return (
    <div className="inline-info" style={{ display: "block" }}>
      <p>유지했을 때와 비교하면 {signed(evaluation.improvement)}</p>
      <p>
        {evaluation.bestActions.length === 4
          ? "모든 선택의 정산 결과가 같아요."
          : evaluation.gap === 0
            ? "이번 구간에서 가장 좋은 결과를 낸 선택이에요."
            : `이 구간의 최선은 ${evaluation.bestActions.map((action) => names[action]).join(" · ")}였어요. 내 선택과 ${money(evaluation.gap)} 차이예요.`}
      </p>
      <small>공개된 이후 구간을 기준으로 비교한 결과예요.</small>
    </div>
  );
}

export function ChoiceStats({
  stats,
  action,
  automatic = false,
}: {
  stats?: ChoiceStatistics;
  action: string;
  automatic?: boolean;
}) {
  if (!stats) return null;
  return (
    <section
      className="choice-statistics"
      aria-label="다른 사람들의 선택"
      style={{ margin: "20px 0" }}
    >
      <h3>다른 사람들은 어떤 선택을 했을까요?</h3>
      <p className="caption">
        {stats.total.toLocaleString("ko-KR")}명의 첫 선택 · 자동 선택 제외
      </p>
      {stats.total === 0 ? (
        <p>아직 직접 선택한 사람이 없어요.</p>
      ) : (
        stats.choices.map((choice) => (
          <div key={choice.id} style={{ marginTop: 12 }}>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <strong>{choice.label}</strong>
              {!automatic && choice.id === action && <small>내 선택</small>}
              {choice.mostSelected && <small>가장 많은 선택</small>}
              <span style={{ marginLeft: "auto" }}>
                {choice.count}명 · {choice.percent}%
              </span>
            </div>
            <progress
              max={100}
              value={choice.percent}
              aria-label={`${choice.label} ${choice.percent}%`}
              style={{
                width: "100%",
                height: 8,
                accentColor: "var(--accent, #267865)",
              }}
            />
          </div>
        ))
      )}
    </section>
  );
}

export function RoundStanding({
  standing,
}: {
  standing?: DailyStanding | null;
}) {
  if (!standing) return null;
  return (
    <div className="inline-info" style={{ display: "block" }}>
      <strong>
        {standing.final ? "최종 순위" : "현재 순위"} ·{" "}
        {standing.tied ? "공동 " : ""}
        {standing.rank}위
      </strong>
      <p>
        같은 날짜·같은 판을 완료한{" "}
        {standing.participants.toLocaleString("ko-KR")}명 중 내 순위예요.
      </p>
      {!standing.final && (
        <small>다른 참가자가 완료하면 순위가 바뀔 수 있어요.</small>
      )}
    </div>
  );
}
